import { Container, Graphics, type Texture } from 'pixi.js';
import gsap from 'gsap';
import { backgroundFor, loadBackground, loadCardArtFor, loadEnemyArtFor, type EnemyTextures } from '../../app/art';
import { openBackgroundPanel } from '../../dev/bgPanel';
import { audio } from '../../app/audio';
import { isPhone } from '../../app/device';
import { DESIGN } from '../../app/fit';
import { markTutorial } from '../../app/tutorial';
import type { Scene, SceneContext } from '../../app/router';
import type { DamageKind } from '../../engine/events';
import type { Card } from '../../content/schema';
import {
  cardOf, costOf, createCombat, describeResolved, drainEvents, endTurn, explainCard, legalPlays, playCard, respondPrompt, useVial,
  type CombatState, type Content, type EncounterSetup, type HeroSetup, type Unplayable,
} from '../../engine/rules';
import { CARD_H, CARD_W, CardView, type CardDisplay } from '../cards/CardView';
import { DragController } from '../cards/DragController';
import { HandLayout } from '../cards/HandLayout';
import { PileView } from '../cards/PileView';
import { CombatBar } from '../combat/CombatBar';
import { CompanionView } from '../combat/CompanionView';
import { EnemyView } from '../combat/EnemyView';
import { Playback, type World } from '../combat/Playback';
import { PlayerPanel } from '../combat/PlayerPanel';
import { ResourceWidget } from '../combat/ResourceWidget';
import { TrapRow } from '../combat/TrapRow';
import { backdrop } from '../kit/backdrop';
import { copyLayers, DEFAULT_LAYERS, ParallaxBackdrop } from '../kit/parallax';
import { Button } from '../kit/button';
import { Coach } from '../kit/coach';
import { vignetteSprite } from '../fx/vignette';
import { Explainer } from '../kit/explainer';
import { openGameMenu } from '../kit/gameMenu';
import { d, done, enemyTurn, spatial } from '../kit/motion';
import { PALETTE } from '../kit/palette';
import { makeText, STYLE } from '../kit/text';
import { Tooltip } from '../kit/tooltip';

export interface CombatSetup {
  hero: HeroSetup;
  encounter: EncounterSetup;
  seed: string;
  /** Background key in the art manifest; defaults to chapter1. */
  background?: string;
  /** Dev only: open the background tuning panel and let the art set be swapped live. */
  tune?: boolean;
  /** An existing fight to continue (a saved run). `hero`/`encounter`/`seed` are ignored. */
  resume?: CombatState;
  /** After every engine step; the run layer saves here. */
  onStep?: (state: CombatState) => void;
  /** Called when the fight ends; the run layer routes onward. Returns the buttons to show, or nothing for the dev overlay. */
  onEnd?: (result: 'won' | 'lost', state: CombatState) => void;
  /** Hide the dev "Again / Title" overlay and let onEnd take over. */
  quietEnd?: boolean;
  /** Run the guided first-fight walkthrough (the run does this once per browser). */
  tips?: boolean;
}

const DESKTOP = {
  enemyBaseY: 640,
  enemyCenterX: 1330,
  player: { x: 250, y: 420 },
  hand: { centerX: 960, baseY: 945, scale: 0.8, hoverScale: 1.15, maxSpread: 1000 },
  aimSpot: { x: 960, y: 790 },
  draw: { x: 110, y: 960 },
  discard: { x: 1810, y: 960 },
  exhaust: { x: 1690, y: 985 },
  endTurn: { x: 1760, y: 830 },
  playLineY: 730,
  center: { x: 960, y: 520 },
  powers: { x: 250, y: 660 },
  /** Where the energy orb hangs off the hero's corner, and the traps under it. */
  orb: { x: -130, y: 230 },
  traps: 300,
  /** The piles carry numbers; a phone draws them larger. */
  zoom: { pile: 1, exhaust: 0.7 },
  endTurnSize: { width: 240, height: 64 } as { width: number; height: number; fontSize?: number },
};

/**
 * The same table on a phone, where the design space is drawn at about a third
 * of its size: the hero's corner, the piles and the button that ends the turn
 * are drawn half again as large, and moved in from the edges to make room.
 */
const PHONE: typeof DESKTOP = {
  ...DESKTOP,
  player: { x: 250, y: 380 },
  // Beside the portrait, not under it: the bar below the hero is twice as tall.
  orb: { x: 198, y: -18 },
  traps: 344,
  draw: { x: 132, y: 944 },
  discard: { x: 1788, y: 944 },
  exhaust: { x: 1640, y: 968 },
  endTurn: { x: 1730, y: 796 },
  zoom: { pile: 1.4, exhaust: 1 },
  endTurnSize: { width: 300, height: 88, fontSize: 40 },
};

/** Past this, a press on a phone was someone reading the card, not choosing it. */
const HOLD_MS = 400;

const REASONS: Record<Unplayable, string> = {
  'not-your-turn': 'Not your turn.',
  'prompt-open': 'Finish choosing first.',
  resolving: 'Wait for the card to resolve.',
  'not-in-hand': '',
  unplayable: 'This card cannot be played.',
  bound: 'Bound: take damage to free it.',
  energy: 'Not enough energy.',
  'needs-target': 'Drag it onto an enemy.',
  'bad-target': 'Not a valid target.',
  condition: 'Its condition is not met.',
};

/** The table. One fight, start to finish. */
export function combatScene(ctx: SceneContext, content: Content, setup: CombatSetup): Scene {
  // Read here, not at the top of the file: the layout is settled during boot,
  // after this module is imported.
  const LAYOUT = isPhone() ? PHONE : DESKTOP;
  const view = new Container({ label: 'combat' });
  let state: CombatState;
  let busy = true;
  let promptSelection: number[] = [];

  const tooltip = new Tooltip();
  const explainer = new Explainer(content, ctx.stage);
  const layers = {
    bg: new Container({ label: 'bg' }),
    table: new Container({ label: 'table' }),
    enemies: new Container({ label: 'enemies' }),
    hand: null as unknown as HandLayout,
    floating: new Container({ label: 'floating' }),
    fx: new Container({ label: 'fx' }),
    ui: new Container({ label: 'ui' }),
    /** Full-screen flashes; above the table, not shaken, under banners. */
    screenFx: new Container({ label: 'screen-fx' }),
    overlay: new Container({ label: 'combat-overlay' }),
  };
  const enemies = new Map<string, EnemyView>();
  let player: PlayerPanel;
  let resource: ResourceWidget | null = null;
  let companion: CompanionView | null = null;
  let traps: TrapRow;
  let bar: CombatBar;
  let piles: World['piles'];
  let playback: Playback;
  let drag: DragController;
  let endTurnBtn: Button;
  let promptBar: Container | null = null;
  /** Closes the card a finger is holding up to read; see showPeek. */
  let closePeek: (() => void) | null = null;
  // The art set is a setting, so a fight picks it up the next time it starts.
  let bgKey = backgroundFor(setup.background ?? 'chapter1', ctx.settings.get().artwork);
  let overlayInFront = true;
  const tuning = copyLayers(DEFAULT_LAYERS);
  let closeTuner: (() => void) | null = null;
  let menuClose: (() => void) | null = null;
  let coach: Coach | null = null;
  const shakeRoot = new Container({ label: 'shake' });
  const vignette = vignetteSprite();
  let enemyArt = new Map<string, EnemyTextures>();
  let cardArt = new Map<string, Texture>();

  function display(uid: number): CardDisplay | null {
    const inst = [...state.piles.hand, ...state.piles.draw, ...state.piles.discard, ...state.piles.exhaust, ...(state.inPlay ? [state.inPlay] : [])].find((c) => c.uid === uid);
    if (!inst) return null;
    const resolved = cardOf(content, inst);
    const inHand = state.piles.hand.includes(inst);
    const cost = resolved.cost === 'X' ? ('X' as const) : { value: inHand ? costOf(state, inst, resolved) : resolved.cost, base: resolved.cost };
    // Cards the deck preload missed — a Secret stolen mid-fight — fetch their own.
    return { resolved, segments: describeResolved(resolved, { state }), cost, art: cardArt.get(inst.cardId) };
  }

  function cardDef(uid: number): Card | null {
    const inst = [...state.piles.hand, ...state.piles.draw, ...state.piles.discard, ...state.piles.exhaust, ...(state.inPlay ? [state.inPlay] : [])].find((c) => c.uid === uid);
    return inst ? (content.cards[inst.cardId] ?? null) : null;
  }

  function makeCardView(uid: number): CardView | null {
    const def = cardDef(uid);
    const disp = display(uid);
    if (!def || !disp) return null;
    const cv = new CardView(uid, def, disp);
    drag.attach(cv);
    return cv;
  }

  /** Re-render hand texts (live numbers) and glow the playable ones. */
  function refreshHand(): void {
    bar?.sync(state.hero);
    const legal = new Set(legalPlays(state, content).map((p) => p.uid));
    for (const cv of layers.hand.cards) {
      const disp = display(cv.cardUid);
      if (disp) cv.refresh(disp);
      cv.setGlow(!busy && !state.prompt && legal.has(cv.cardUid));
    }
    endTurnBtn.alpha = busy ? 0.5 : 1;
    // Nothing left to do this turn: point at End Turn.
    endTurnBtn.setGlow(!busy && !state.prompt && state.phase === 'player' && (state.hero.energy === 0 || legal.size === 0));
    if (state.hero.energy === 0) coach?.notify('energy-zero');
    maybeAutoEndTurn(legal.size);
  }

  /**
   * End the turn without being asked, for players who have opted in. It waits for
   * an empty energy orb *and* nothing playable — a 0-cost card in hand is still a
   * turn worth taking — and never fires while the coach is talking, so the
   * walkthrough's "end your turn" step is still the player's to do.
   */
  function maybeAutoEndTurn(legalPlayCount: number): void {
    if (!ctx.settings.get().autoEndTurn || coach) return;
    if (busy || state.prompt || state.phase !== 'player') return;
    if (state.hero.energy > 0 || legalPlayCount > 0) return;
    // A beat to see the board settle before it moves on.
    gsap.delayedCall(d(0.55), () => {
      if (busy || state.prompt || state.phase !== 'player') return;
      if (state.hero.energy > 0 || legalPlays(state, content).length > 0) return;
      doEndTurn();
    });
  }

  function layoutEnemies(): void {
    const alive = state.enemies.filter((e) => e.alive && enemies.has(e.id));
    const widths = alive.map((e) => ({ small: 220, medium: 300, large: 380 })[content.enemies[e.enemyId]!.size]);
    const total = widths.reduce((a, b) => a + b, 0);
    let x = LAYOUT.enemyCenterX - total / 2;
    alive.forEach((e, i) => {
      const v = enemies.get(e.id)!;
      const cx = x + widths[i]! / 2;
      x += widths[i]!;
      gsap.to(v, { x: cx, y: LAYOUT.enemyBaseY, duration: d(0.3), ease: 'power2.out' });
    });
  }

  function addEnemy(id: string): EnemyView | null {
    const inst = state.enemies.find((e) => e.id === id);
    if (!inst) return null;
    const def = content.enemies[inst.enemyId]!;
    const v = new EnemyView(inst, def, enemyArt.get(inst.enemyId) ?? null, explainer, layers.fx);
    v.position.set(LAYOUT.enemyCenterX, LAYOUT.enemyBaseY);
    enemies.set(id, v);
    layers.enemies.addChild(v);
    v.body.on('pointerover', () => {
      if (!drag.isDragging) v.setHighlight(true);
    });
    v.body.on('pointerout', () => {
      if (!drag.isDragging) v.setHighlight(false);
    });
    v.body.on('rightclick', () => showMoves(id));
    v.body.on('pointertap', () => drinkOn(id));
    return v;
  }

  /** Right-click an enemy: the moves it has shown so far, with their numbers. */
  function showMoves(id: string): void {
    const inst = state.enemies.find((e) => e.id === id);
    if (!inst) return;
    const def = content.enemies[inst.enemyId]!;
    const seen = [...new Set(inst.history)];
    const overlay = new Container();
    const dim = new Graphics();
    dim.rect(0, 0, DESIGN.width, DESIGN.height).fill({ color: 0x000000, alpha: 0.6 });
    dim.eventMode = 'static';
    dim.on('pointertap', () => overlay.destroy({ children: true }));
    dim.on('rightclick', () => overlay.destroy({ children: true }));
    overlay.addChild(dim);
    const panel = new Graphics();
    panel.roundRect(0, 0, 640, 120 + Math.max(1, seen.length) * 44, 16).fill({ color: 0x14121c, alpha: 0.97 }).stroke({ color: PALETTE.gold, width: 2 });
    panel.position.set(DESIGN.width / 2 - 320, 300);
    overlay.addChild(panel);
    const title = makeText(def.name.toUpperCase(), { ...STYLE.display(30), fill: PALETTE.gold });
    title.position.set(panel.x + 28, panel.y + 22);
    overlay.addChild(title);
    const artist = enemies.get(id)?.artist;
    const credit = artist ? content.credits?.[artist]?.name ?? artist : null;
    const sub = makeText(`${def.tags.join(' · ')} · moves seen ${seen.length} of ${Object.keys(def.moves).length}${credit ? ` · drawn by ${credit}` : ''}`, { ...STYLE.mono(16), fill: PALETTE.parchmentDim });
    sub.position.set(panel.x + 28, panel.y + 66);
    overlay.addChild(sub);
    const lines = seen.length ? seen : ['—'];
    lines.forEach((m, i) => {
      const move = def.moves[m];
      const name = move?.name ?? m.split('-').map((w) => w[0]!.toUpperCase() + w.slice(1)).join(' ');
      const kind = move ? move.intent : '';
      const text = makeText(`${name}${kind ? `  ·  ${kind}` : ''}`, STYLE.body(24));
      text.position.set(panel.x + 28, panel.y + 100 + i * 44);
      overlay.addChild(text);
    });
    layers.overlay.addChild(overlay);
  }

  /** `hold` multiplies how long it sits at full alpha: the end of a fight lingers. */
  async function banner(text: string, sub?: string, hold = 1): Promise<void> {
    if (!text && !sub) return;
    const c = new Container();
    const bg = new Graphics();
    bg.rect(0, -50, DESIGN.width, 100).fill({ color: 0x000000, alpha: text ? 0.55 : 0.25 });
    c.addChild(bg);
    if (text) {
      const t = makeText(text, { ...STYLE.display(64), fill: PALETTE.gold, letterSpacing: 8 });
      t.anchor.set(0.5);
      c.addChild(t);
    }
    if (sub) {
      const s = makeText(sub, { ...STYLE.mono(20), fill: PALETTE.parchmentDim });
      s.anchor.set(0.5);
      s.position.set(0, text ? 46 : 0);
      c.addChild(s);
    }
    c.position.set(DESIGN.width / 2, LAYOUT.center.y - 60);
    c.alpha = 0;
    layers.overlay.addChild(c);
    const tl = gsap.timeline({ onComplete: () => c.destroy({ children: true }) });
    const visible = (text ? 0.26 : 0.13) * hold;
    tl.to(c, { alpha: 1, duration: d(0.12) }).to(c, { alpha: 0, duration: d(0.2) }, d(0.12 + visible));
    await done(tl);
  }

  /** Rattle the whole table; `intensity` 0..1 sets how far and how long. */
  function shakeScreen(intensity: number): void {
    if (!spatial() || !ctx.settings.get().shake || intensity <= 0) return;
    const px = 3 + 18 * intensity;
    const n = 3 + Math.round(4 * intensity);
    gsap.killTweensOf(shakeRoot);
    const tl = gsap.timeline({ onComplete: () => shakeRoot.position.set(0, 0) });
    for (let i = 0; i < n; i++) {
      const s = px * (1 - i / n);
      tl.to(shakeRoot, { x: (Math.random() - 0.5) * 2 * s, y: (Math.random() - 0.5) * 1.4 * s, duration: d(0.035) });
    }
    tl.to(shakeRoot, { x: 0, y: 0, duration: d(0.05) });
  }

  const FLASH_COLOR: Record<DamageKind, number> = { attack: 0xc8102e, poison: 0x6fbf3f, effect: PALETTE.ember };

  /** The hero was hit: a diffuse flash from the edges, coloured by what hit them. */
  function screenFlash(kind: DamageKind, intensity: number): void {
    if (intensity <= 0) return;
    vignette.tint = FLASH_COLOR[kind];
    gsap.killTweensOf(vignette);
    vignette.alpha = 0.3 + 0.55 * intensity;
    gsap.to(vignette, { alpha: 0, duration: Math.max(0.2, d(0.3 + 0.5 * intensity)), ease: 'power2.out' });
  }

  async function slowMo(): Promise<void> {
    if (!spatial()) return;
    gsap.globalTimeline.timeScale(0.25);
    await new Promise((r) => setTimeout(r, 90));
    gsap.globalTimeline.timeScale(1);
  }

  /** Run an engine action, then play what it produced. */
  async function act(fn: () => void): Promise<void> {
    if (busy) return;
    busy = true;
    refreshHand();
    const turnBefore = state.turn;
    fn();
    const events = drainEvents(state);
    setup.onStep?.(state);
    await playback.play(events);
    busy = false;
    if (state.turn > turnBefore) coach?.notify('hero-turn');
    if (state.phase === 'won' || state.phase === 'lost') {
      busy = true;
      return;
    }
    if (state.prompt) showPrompt();
    refreshHand();
  }

  /**
   * A pile spread over the board, so a retrieve prompt has something to pick
   * from: those cards are in the discard or exhaust pile, not in the hand.
   * The grid takes the fewest rows that let the cards be biggest.
   */
  function pileGrid(uids: number[]): CardView[] {
    const AREA = { y: 110, w: DESIGN.width - 160, h: LAYOUT.playLineY - 170 };
    const GAP = 18;
    let cols = uids.length;
    let rows = 1;
    let scale = 0;
    for (let r = 1; r <= uids.length; r++) {
      const c = Math.ceil(uids.length / r);
      const s = Math.min(0.62, (AREA.w / c - GAP) / CARD_W, (AREA.h / r - GAP) / CARD_H);
      if (s > scale) [scale, cols, rows] = [s, c, r];
    }
    const out: CardView[] = [];
    uids.forEach((uid, i) => {
      const def = cardDef(uid);
      const disp = display(uid);
      if (!def || !disp) return;
      const cv = new CardView(uid, def, disp);
      cv.scale.set(scale);
      cv.cursor = 'pointer';
      const row = Math.floor(i / cols);
      const inRow = Math.min(cols, uids.length - row * cols);
      cv.position.set(DESIGN.width / 2 + (i - row * cols - (inRow - 1) / 2) * (CARD_W * scale + GAP), AREA.y + AREA.h / 2 + (row - (rows - 1) / 2) * (CARD_H * scale + GAP));
      out.push(cv);
    });
    return out;
  }

  /**
   * Choosing cards for an effect. Discard and exhaust pick from the hand,
   * which is already on the table; retrieve picks from a pile the player
   * cannot see, so that pile is laid out over the board to choose from.
   */
  function showPrompt(): void {
    const p = state.prompt!;
    promptSelection = [];
    promptBar = new Container();
    const picks = p.kind === 'retrieve' ? pileGrid(p.from) : layers.hand.cards;
    if (p.kind === 'retrieve') {
      const dim = new Graphics();
      dim.rect(0, 0, DESIGN.width, DESIGN.height).fill({ color: 0x000000, alpha: 0.72 });
      dim.eventMode = 'static';
      promptBar.addChild(dim, ...picks);
      layers.overlay.addChild(promptBar);
    } else {
      layers.ui.addChild(promptBar);
    }
    const bar = new Container();
    const bg = new Graphics();
    bg.roundRect(-360, -34, 720, 68, 12).fill({ color: 0x000000, alpha: 0.7 }).stroke({ color: PALETTE.gold, width: 2 });
    bar.addChild(bg);
    const label = makeText(`Choose ${p.count} card${p.count > 1 ? 's' : ''} to ${p.kind}`, { ...STYLE.display(24), fill: PALETTE.gold });
    label.anchor.set(0, 0.5);
    label.position.set(-330, 0);
    bar.addChild(label);
    const confirm = new Button({
      label: 'Confirm',
      width: 180,
      height: 48,
      onPress: () => {
        if (promptSelection.length !== p.count) return;
        const chosen = [...promptSelection];
        promptBar?.destroy({ children: true });
        promptBar = null;
        void act(() => respondPrompt(state, content, chosen));
      },
    });
    confirm.position.set(250, 0);
    confirm.alpha = 0.5;
    bar.addChild(confirm);
    bar.position.set(DESIGN.width / 2, LAYOUT.playLineY + 30);
    promptBar.addChild(bar);
    for (const cv of picks) {
      cv.removeAllListeners('pointertap');
      let pressedAt = 0;
      if (isPhone()) {
        // The cards laid out from a pile are not in the hand, so nothing else
        // is offering to show them; and a hold is a read, not a choice.
        cv.on('pointerdown', () => {
          pressedAt = performance.now();
          if (p.kind === 'retrieve') showPeek(cv);
        });
      }
      cv.on('pointertap', () => {
        if (!state.prompt) return;
        if (isPhone() && performance.now() - pressedAt > HOLD_MS) return;
        const i = promptSelection.indexOf(cv.cardUid);
        if (i >= 0) promptSelection.splice(i, 1);
        else if (promptSelection.length < p.count) promptSelection.push(cv.cardUid);
        for (const c of picks) c.setGlow(promptSelection.includes(c.cardUid));
        confirm.alpha = promptSelection.length === p.count ? 1 : 0.5;
      });
    }
  }

  /**
   * The card, big enough to read, with its words explained beside it. A tap
   * opens it and a tap closes it; a `transient` one belongs to the finger
   * holding a card on a phone and is closed by letting go, so it takes no
   * pointer events of its own and shows no closing hint. Returns its close.
   */
  function showInspector(uid: number, transient = false): (() => void) | null {
    const def = cardDef(uid);
    const disp = display(uid);
    if (!def || !disp) return null;
    const overlay = new Container();
    const dim = new Graphics();
    dim.rect(0, 0, DESIGN.width, DESIGN.height).fill({ color: 0x000000, alpha: transient ? 0.55 : 0.7 });
    dim.eventMode = transient ? 'none' : 'static';
    const close = () => {
      dim.eventMode = 'none';
      explainer.hide();
      gsap.to(overlay, { alpha: 0, duration: d(0.12), onComplete: () => overlay.destroy({ children: true }) });
    };
    if (!transient) dim.on('pointertap', close);
    overlay.addChild(dim);
    const big = new CardView(uid, def, disp);
    // The tap-to-open one leaves the right-hand side clear for its notes; the
    // held one is read and let go, so it sits in the middle.
    const cx = transient ? DESIGN.width / 2 : DESIGN.width / 2 - 200;
    big.position.set(cx, DESIGN.height / 2);
    big.eventMode = 'none';
    overlay.addChild(big);
    // Grow in from a little smaller, over the fading dim: one motion, not a lift and a drop.
    overlay.alpha = 0;
    gsap.to(overlay, { alpha: 1, duration: d(0.15) });
    gsap.fromTo(big.scale, { x: 1.9, y: 1.9 }, { x: 2.2, y: 2.2, duration: d(0.2), ease: 'power2.out' });
    // Its words, explained beside it; the same column a hover shows.
    const ex = explainCard(content, disp.resolved, { state });
    if (ex.notes.length) explainer.showAt({ x: cx - CARD_W * 1.1, y: DESIGN.height / 2 - CARD_H * 1.1, w: CARD_W * 2.2, h: CARD_H * 2.2 }, ex, { header: false, delayMs: 0 });
    else if (!transient) {
      const none = makeText('Nothing here needs explaining.', { ...STYLE.body(22), fill: PALETTE.parchmentDim });
      none.position.set(DESIGN.width / 2 + 120, DESIGN.height / 2 - 20);
      overlay.addChild(none);
    }
    if (!transient) {
      const hint = makeText('tap or click anywhere to close', STYLE.mono(16));
      hint.alpha = 0.6;
      hint.anchor.set(0.5);
      hint.position.set(DESIGN.width / 2, DESIGN.height - 60);
      overlay.addChild(hint);
    }
    layers.overlay.addChild(overlay);
    return close;
  }

  /**
   * Phone: a card in the fan is 70 pixels wide, so the finger landing on one
   * shows it at a size it can be read at, until the finger leaves or starts
   * dragging it somewhere.
   */
  function showPeek(view: CardView | null): void {
    closePeek?.();
    closePeek = view ? showInspector(view.cardUid, true) : null;
  }

  function openMenu(): void {
    if (menuClose) return;
    menuClose = openGameMenu(layers.overlay, { router: ctx.router, onClose: () => { menuClose = null; } });
  }

  function doEndTurn(): void {
    if (busy || state.prompt) return;
    coach?.notify('end-turn');
    audio().play('turn-end');
    void act(() => endTurn(state, content));
  }

  /**
   * The first-fight walkthrough. Each step waits for the player to do the
   * thing it describes; the chevrons follow the card, the orb, the enemy.
   */
  function startCoach(): void {
    const firstCard = (targeted: boolean) => {
      const legal = new Set(legalPlays(state, content).map((p) => p.uid));
      const cv = layers.hand.cards.find((c) => legal.has(c.cardUid) && (cardDef(c.cardUid)?.target === 'enemy') === targeted);
      return cv ? { x: layers.hand.x + cv.x, y: layers.hand.y + cv.y - (CARD_H * cv.scale.y) / 2 } : null;
    };
    const firstEnemy = () => [...enemies.values()].find((v) => !v.dead) ?? null;
    const orbTop = { x: LAYOUT.player.x + LAYOUT.orb.x, y: LAYOUT.player.y + LAYOUT.orb.y - 58 };
    const endTurnTop = { x: LAYOUT.endTurn.x, y: LAYOUT.endTurn.y - 36 };
    coach = new Coach(ctx.stage, [
      {
        title: 'Attack',
        text: 'Attack cards need a target. Drag the glowing card onto an enemy and let go.',
        until: ['play-targeted'],
        point: () => [firstCard(true), firstEnemy()?.top ?? null],
      },
      {
        title: 'Energy',
        text: 'Every card costs energy: the number in its corner. The orb shows what you have left this turn; it refills every turn. Skills need no target. Drag one straight up, past the line.',
        until: ['play-untargeted', 'energy-zero'],
        point: () => [orbTop, firstCard(false)],
      },
      {
        title: 'What the enemy will do',
        text: 'The badge above an enemy is its next move. A sword means an attack, and the number is the damage it will deal. Block from your skills soaks damage before it reaches your health, and lasts until your next turn.',
        point: () => [firstEnemy()?.top ?? null],
      },
      {
        title: 'The words in gold',
        text: isPhone()
          ? 'Anything written in gold has a meaning you can check. Hold a card down to see it large, with its words explained beside it; touch a badge or a status for the same.'
          : 'Anything written in gold has a meaning you can check. Rest the pointer on a card, a badge or a status for a moment (tap it on a touch screen) and its words are explained beside it.',
        until: ['explain'],
        point: () => [firstCard(false) ?? firstCard(true), firstEnemy()?.top ?? null],
      },
      {
        title: 'End your turn',
        text: 'Out of energy, or out of moves? Press End Turn. Cards you did not play go to the discard pile.',
        until: ['end-turn'],
        point: () => [endTurnTop],
      },
      {
        title: 'Your turn again',
        text: isPhone()
          ? 'A new hand of five and full energy, every turn. When the draw pile runs out, the discard pile shuffles back in. Hold any card to read it. Now win this fight.'
          : 'A new hand of five and full energy, every turn. When the draw pile runs out, the discard pile shuffles back in. Hover anything for details. Right-click an enemy to see the moves it has shown. Now win this fight.',
        after: 'hero-turn',
      },
    ], { x: 60, y: 64 }, () => {
      markTutorial('fight');
      coach = null;
    });
    layers.overlay.addChild(coach);
    coach.start();
  }

  function endFight(result: 'won' | 'lost'): void {
    setup.onStep?.(state);
    if (setup.quietEnd) {
      setup.onEnd?.(result, state);
      return;
    }
    const overlay = new Container();
    const dim = new Graphics();
    dim.rect(0, 0, DESIGN.width, DESIGN.height).fill({ color: 0x000000, alpha: 0.6 });
    overlay.addChild(dim);
    const t = makeText(result === 'won' ? 'VICTORY' : 'DEFEAT', { ...STYLE.display(96), fill: PALETTE.gold, letterSpacing: 10 });
    t.anchor.set(0.5);
    t.position.set(DESIGN.width / 2, 400);
    overlay.addChild(t);
    const again = new Button({ label: 'Again', onPress: () => ctx.router.go('/dev/fight', { seed: `${setup.seed}-${Date.now() % 1000}`, class: setup.hero.classId, enemies: setup.encounter.enemies.join(',') }) });
    again.position.set(DESIGN.width / 2, 560);
    const back = new Button({ label: 'Title', variant: 'ghost', onPress: () => ctx.router.go('/') });
    back.position.set(DESIGN.width / 2, 660);
    overlay.addChild(again, back);
    overlay.alpha = 0;
    layers.overlay.addChild(overlay);
    gsap.to(overlay, { alpha: 1, duration: d(0.4) });
    setup.onEnd?.(result, state);
  }

  let fpsText: ReturnType<typeof makeText> | null = null;
  function toggleFps(): void {
    if (fpsText) {
      ctx.stage.app.ticker.remove(fpsTick);
      fpsText.destroy();
      fpsText = null;
      return;
    }
    fpsText = makeText('', { ...STYLE.mono(22), fill: PALETTE.goldBright });
    fpsText.position.set(24, 24);
    layers.overlay.addChild(fpsText);
    ctx.stage.app.ticker.add(fpsTick);
  }
  let worst = 0;
  function fpsTick(): void {
    if (!fpsText) return;
    const t = ctx.stage.app.ticker;
    worst = Math.max(worst * 0.97, t.deltaMS);
    fpsText.text = `${t.FPS.toFixed(0)} fps · ${t.deltaMS.toFixed(1)} ms · worst ${worst.toFixed(1)} · hand ${layers.hand.cards.length} · enemies ${enemies.size}`;
  }

  /** Keyboard target: index into the living enemies, shown as the ground ring. */
  let keyTarget = 0;
  function keyTargetId(): string | undefined {
    const alive = state.enemies.filter((x) => x.alive);
    if (!alive.length) return undefined;
    keyTarget = ((keyTarget % alive.length) + alive.length) % alive.length;
    return alive[keyTarget]!.id;
  }
  function showKeyTarget(): void {
    const id = keyTargetId();
    for (const [eid, v] of enemies) v.setHighlight(eid === id);
  }

  function onKey(e: KeyboardEvent): void {
    if (e.key === 'f' || e.key === 'F') toggleFps();
    if (e.key === 'Escape') {
      if (menuClose) {
        menuClose();
        menuClose = null;
        return;
      }
      layers.hand.setHover(null);
      bar.selectedVial = null;
      bar.sync(state.hero);
      for (const v of enemies.values()) v.setHighlight(false);
    }
    if (busy || state.prompt) return;
    if (e.key === 'e' || e.key === 'E') doEndTurn();
    const n = Number(e.key);
    if (n >= 1 && n <= 9) {
      const cv = layers.hand.cards[n - 1];
      if (cv) layers.hand.setHover(cv);
    }
    if (e.key === 'ArrowLeft' || e.key === 'ArrowRight') {
      keyTarget += e.key === 'ArrowLeft' ? -1 : 1;
      showKeyTarget();
    }
    if (e.key === 'Enter' && layers.hand.hovered) {
      const uid = layers.hand.hovered.cardUid;
      const targeted = content.cards[layers.hand.hovered.card.id]!.target === 'enemy';
      void tryPlay(uid, targeted ? keyTargetId() : undefined);
      for (const v of enemies.values()) v.setHighlight(false);
    }
  }

  /** A vial: drink it, or arm it and wait for an enemy click. */
  function onVial(index: number): void {
    if (busy || state.prompt) return;
    const id = state.hero.vials[index];
    const vial = id ? content.vials?.[id] : undefined;
    if (!vial) return;
    if (vial.target === 'enemy') {
      bar.selectedVial = bar.selectedVial === index ? null : index;
      bar.sync(state.hero);
      for (const v of enemies.values()) v.setHighlight(bar.selectedVial !== null);
      return;
    }
    void act(() => useVial(state, content, index));
    bar.selectedVial = null;
  }

  function drinkOn(enemyId: string): boolean {
    if (bar.selectedVial === null) return false;
    const index = bar.selectedVial;
    bar.selectedVial = null;
    for (const v of enemies.values()) v.setHighlight(false);
    void act(() => useVial(state, content, index, enemyId));
    return true;
  }

  function tryPlay(uid: number, targetId?: string): boolean {
    const r = playCard(state, content, uid, targetId);
    if (!r.ok) {
      if (r.reason === 'energy') player.refuseEnergy();
      const msg = REASONS[r.reason];
      if (msg) {
        const p = layers.hand.find(uid)?.getGlobalPosition();
        const local = p ? tooltip.parent!.toLocal(p) : { x: 960, y: 800 };
        tooltip.show('', msg, local.x, local.y - 60, 0);
        setTimeout(() => tooltip.hide(), 1200);
      }
      return false;
    }
    // The engine has already run; play it back.
    coach?.notify(targetId ? 'play-targeted' : 'play-untargeted');
    busy = true;
    refreshHand();
    const events = drainEvents(state);
    setup.onStep?.(state);
    void playback.play(events).then(() => {
      busy = false;
      if (state.phase === 'won' || state.phase === 'lost') {
        busy = true;
        return;
      }
      if (state.prompt) showPrompt();
      refreshHand();
    });
    return true;
  }

  return {
    view,
    async enter() {
      const resumed = !!setup.resume;
      state = setup.resume ?? createCombat(content, setup.hero, setup.encounter, setup.seed);
      // Art for this fight only: the enemies in it and the cards in the deck.
      const deckIds = [...state.piles.draw, ...state.piles.hand, ...state.piles.discard].map((c) => c.cardId);
      const [ea, ca, bg] = await Promise.all([loadEnemyArtFor(state.enemies.map((e) => e.enemyId)), loadCardArtFor(deckIds), loadBackground(bgKey)]);
      enemyArt = ea;
      cardArt = ca;

      view.addChild(shakeRoot);
      shakeRoot.addChild(layers.bg, layers.table, layers.enemies);
      // The watercolour overlay, when the art has one, sits over the table and the
      // enemies but under every piece of interface.
      let parallax = bg ? new ParallaxBackdrop(bg, ctx.stage) : null;
      layers.bg.addChild(parallax ?? backdrop(0x161a24, 0x0b0a0f));
      const placeOverlay = () => {
        const o = parallax?.overlay;
        if (!o) return;
        o.removeFromParent();
        // In front of the enemies, or behind them; either way under the hand and the UI.
        if (overlayInFront) shakeRoot.addChildAt(o, shakeRoot.getChildIndex(layers.enemies) + 1);
        else shakeRoot.addChildAt(o, shakeRoot.getChildIndex(layers.enemies));
      };
      placeOverlay();
      if (setup.tune && import.meta.env.DEV) {
        closeTuner = openBackgroundPanel({
          settings: tuning,
          apply: () => parallax?.set(tuning),
          key: bgKey,
          setKey: (k) => {
            bgKey = k;
            void loadBackground(k).then((tex) => {
              if (!tex) return;
              parallax?.destroy({ children: true });
              parallax = new ParallaxBackdrop(tex, ctx.stage, tuning);
              layers.bg.addChild(parallax);
              placeOverlay();
            });
          },
          overlayInFront,
          setOverlayInFront: (v) => {
            overlayInFront = v;
            placeOverlay();
          },
        });
      }
      const floor = new Graphics();
      floor.ellipse(LAYOUT.enemyCenterX, LAYOUT.enemyBaseY + 10, 520, 60).fill({ color: 0x000000, alpha: 0.25 });
      layers.table.addChild(floor);

      layers.hand = new HandLayout(LAYOUT.hand);
      shakeRoot.addChild(layers.hand, layers.floating, layers.fx, layers.ui);
      view.addChild(layers.screenFx, layers.overlay);
      layers.screenFx.addChild(vignette);
      ctx.stage.overlay.addChild(tooltip, explainer);
      explainer.onShow = () => coach?.notify('explain');
      // The lifted card explains its words beside it, after the hover delay.
      layers.hand.onHover = (cv) => {
        if (!cv) {
          explainer.hide();
          return;
        }
        const disp = display(cv.cardUid);
        if (disp) explainer.show(cv, explainCard(content, disp.resolved, { state }), { header: false });
      };

      player = new PlayerPanel(state.hero, explainer, layers.fx);
      player.position.set(LAYOUT.player.x, LAYOUT.player.y);
      player.energyOrb.position.set(LAYOUT.orb.x, LAYOUT.orb.y);
      layers.ui.addChild(player);

      const classDef = content.classes?.[state.hero.classId];
      if (classDef?.resource) {
        resource = new ResourceWidget(classDef.resource, explainer);
        resource.set(state.hero.resources[classDef.resource]);
        player.resourceSlot.addChild(resource);
      }
      traps = new TrapRow(content, tooltip);
      traps.position.set(0, LAYOUT.traps);
      player.addChild(traps);
      if (state.hero.companion) {
        companion = new CompanionView(state.hero.companion, tooltip, layers.fx);
        companion.position.set(LAYOUT.player.x + 250, LAYOUT.player.y + 220);
        layers.ui.addChild(companion);
      }

      piles = { draw: new PileView('draw', PALETTE.gold), discard: new PileView('discard', PALETTE.blood), exhaust: new PileView('exhaust', PALETTE.type.status) };
      piles.draw.position.set(LAYOUT.draw.x, LAYOUT.draw.y);
      piles.discard.position.set(LAYOUT.discard.x, LAYOUT.discard.y);
      piles.exhaust.position.set(LAYOUT.exhaust.x, LAYOUT.exhaust.y);
      piles.draw.scale.set(LAYOUT.zoom.pile);
      piles.discard.scale.set(LAYOUT.zoom.pile);
      piles.exhaust.scale.set(LAYOUT.zoom.exhaust);
      layers.ui.addChild(piles.draw, piles.discard, piles.exhaust);

      bar = new CombatBar(content, explainer, onVial, () => openMenu());
      bar.sync(state.hero);
      layers.ui.addChild(bar);

      endTurnBtn = new Button({ label: 'End Turn', ...LAYOUT.endTurnSize, onPress: doEndTurn });
      endTurnBtn.position.set(LAYOUT.endTurn.x, LAYOUT.endTurn.y);
      layers.ui.addChild(endTurnBtn);

      drag = new DragController({
        hand: layers.hand,
        floating: layers.floating,
        fx: layers.fx,
        stage: ctx.stage,
        canInteract: () => !busy && !state.prompt,
        isTargeted: (uid) => cardDef(uid)?.target === 'enemy',
        enemyAt: (x, y) => {
          for (const [id, v] of enemies) {
            if (v.dead) continue;
            const local = v.body.toLocal({ x, y }, shakeRoot);
            if (v.body.hitArea?.contains(local.x, local.y)) return id;
          }
          return null;
        },
        enemyCenter: (id) => {
          const v = enemies.get(id);
          return v && !v.dead ? v.center : null;
        },
        aimSpot: LAYOUT.aimSpot,
        highlightEnemy: (id) => {
          for (const [eid, v] of enemies) v.setHighlight(eid === id);
        },
        playLineY: LAYOUT.playLineY,
        onPlay: (uid, targetId) => tryPlay(uid, targetId),
        onInspect: (uid) => void showInspector(uid),
        onPeek: (view) => showPeek(view),
        onReorder: (order) => {
          // The hand's order is presentational; write it back so draws keep it.
          state.piles.hand.sort((a, b) => order.indexOf(a.uid) - order.indexOf(b.uid));
        },
      });

      for (const e of state.enemies) if (e.alive) addEnemy(e.id);
      layoutEnemies();
      for (const v of enemies.values()) v.position.set(v.x, v.y);

      playback = new Playback({
        get state() {
          return state;
        },
        content,
        hand: layers.hand,
        floating: layers.floating,
        fx: layers.fx,
        player,
        resource,
        companion,
        traps,
        enemies,
        piles,
        makeCardView,
        addEnemy,
        layoutEnemies,
        banner,
        shakeScreen,
        screenFlash,
        slowMo,
        positions: { draw: LAYOUT.draw, discard: LAYOUT.discard, exhaust: LAYOUT.exhaust, center: LAYOUT.center, powers: LAYOUT.powers },
        onEnd: endFight,
      });
      playback.reset();

      window.addEventListener('keydown', onKey);
      if (import.meta.env.DEV) {
        const dev = (window as unknown as { __tome?: Record<string, unknown> }).__tome;
        if (dev) dev.combat = { get state() { return state; }, content, get busy() { return busy; }, hand: layers.hand, enemies, get resource() { return resource; } };
      }
      if (resumed && state.events.length === 0) {
        // A saved fight mid-way: rebuild the hand from state.
        for (const inst of state.piles.hand) {
          const cv = makeCardView(inst.uid);
          if (cv) layers.hand.add(cv);
        }
        layers.hand.layout(true);
        playback.syncCounts();
        player.sync(state.hero);
        for (const e of state.enemies) {
          const v = enemies.get(e.id);
          if (!v || !e.alive) continue;
          v.setHp(e.hp, e.maxHp, e.block);
          v.setIntent(e.intent);
          for (const [k, n] of Object.entries(e.statuses)) v.statuses.set(k as keyof typeof e.statuses, n ?? 0);
        }
        layoutEnemies();
        busy = false;
        if (state.prompt) showPrompt();
        refreshHand();
        if (setup.tips) startCoach();
        return;
      }
      // The opening: the engine already drew; play those events.
      const events = drainEvents(state);
      await playback.play(events);
      busy = false;
      refreshHand();
      if (setup.tips) startCoach();
    },
    exit() {
      window.removeEventListener('keydown', onKey);
      closeTuner?.();
      enemyTurn(false);
      drag?.dispose();
      if (fpsText) toggleFps();
      tooltip.destroy({ children: true });
      explainer.destroy({ children: true });
      gsap.globalTimeline.timeScale(1);
    },
  };
}
